import React, { useState, useEffect } from "react";
import { message, Button, Modal } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { getGa4OAuthRedirect } from "src/config/Endpoints";
import { ga4Service } from "src/services/GoogleAnalyticsAPIs";

const GA4_OAUTH_REDIRECT = getGa4OAuthRedirect();

interface GA4ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
}

const GA4Modal: React.FC<GA4ModalProps> = ({
  isOpen,
  onClose,
  onConnected,
}) => {
  const api_key = localStorage.getItem("api_key");
  const tenant_id = localStorage.getItem("tenant_id");
  const user_id = localStorage.getItem("user_id");

  const [isLoading, setIsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedProperty, setSelectedProperty] = useState("");
  const [metadataCache, setMetadataCache] = useState("disabled");
  const [errorMessage, setErrorMessage] = useState("");
  const [accounts, setAccounts] = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [isGA4Connected, setIsGA4Connected] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // ============================
  // Fetch Existing Connection
  // ============================
  useEffect(() => {
    const fetchExistingGA4Details = async () => {
      try {
        const result = await ga4Service.fetchExistingConnection(
          tenant_id,
          api_key
        );

        if (result.status && result.ga4_account_id && result.ga4_property_id) {
          setIsGA4Connected(true);

          const accountOption = {
            name: result.ga4_account_id,
            displayNameWithName: result.ga4_account_id,
          };

          const propertyOption = {
            name: `properties/${result.ga4_property_id}`,
            displayNameWithName: result.ga4_property_id,
          };

          setAccounts([accountOption]);
          setProperties([propertyOption]);
          setSelectedAccount(result.ga4_account_id);
          setSelectedProperty(`properties/${result.ga4_property_id}`);
          setMetadataCache(result.ga4_metadata_status);
          setAccessToken(result.ga4_refresh_token);
        } else {
          setIsGA4Connected(false);
        }
      } catch {
        setIsGA4Connected(false);
        setErrorMessage("Error fetching existing connection.");
      }
    };

    if (isOpen) {
      fetchExistingGA4Details();
    }
  }, [isOpen, api_key, tenant_id]);

  // ============================
  // Google OAuth
  // ============================
  const signInWithGoogle = () => {
    const CLIENT_ID =
      "996633909190-2nnsu9ssi3kmjtkct4dsvt9e7hdgn1fj.apps.googleusercontent.com";
    const oauthUrl =
      "https://accounts.google.com/o/oauth2/v2/auth?" +
      `client_id=${CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(GA4_OAUTH_REDIRECT)}` +
      "&response_type=code" +
      "&scope=https://www.googleapis.com/auth/analytics.readonly" +
      "&access_type=offline" +
      "&prompt=consent";

    const handleOAuthMessage = (event: MessageEvent) => {
      if (event.data.access_token && event.data.refresh_token) {
        setAccessToken(event.data.access_token);
        setRefreshToken(event.data.refresh_token);
      }
    };

    window.addEventListener("message", handleOAuthMessage);
    window.open(oauthUrl, "oauthWindow", "width=600,height=600");
  };

  // ============================
  // Fetch Accounts
  // ============================
  useEffect(() => {
    const fetchAccounts = async () => {
      setErrorMessage("");
      if (!accessToken) {
        setErrorMessage("Please sign in first.");
        return;
      }

      try {
        const data = await ga4Service.fetchAccounts(accessToken);
        const formattedAccounts = data.accounts.map((account: any) => ({
          ...account,
          displayNameWithName: `${account.displayName} (${
            account.name.split("/")[1]
          })`,
        }));
        setAccounts(formattedAccounts);
      } catch {
        setErrorMessage("Error fetching accounts.");
      }
    };

    if (!isGA4Connected && accessToken) {
      fetchAccounts();
    }
  }, [accessToken, isGA4Connected]);

  // ============================
  // Fetch Properties
  // ============================
  const fetchProperties = async (accountName: string) => {
    if (!accessToken || !accountName) {return;}

    const accountId = accountName.includes("/")
      ? accountName.split("/").pop()!
      : accountName;

    try {
      const data = await ga4Service.fetchProperties(accessToken, accountId);
      const formattedProperties = data.properties.map((property: any) => {
        const nameAfterSlash = property.name.split("/")[1];
        return {
          ...property,
          displayNameWithName: `${property.displayName} (${nameAfterSlash})`,
        };
      });
      setProperties(formattedProperties);
    } catch {
      setErrorMessage("Error fetching properties.");
    }
  };

  // ============================
  // Handlers
  // ============================
  const handleAccountSelect = (accountId: string) => {
    const fullAccountName = `accounts/${accountId}`;
    setSelectedAccount(accountId);
    fetchProperties(fullAccountName);
  };

  const handlePropertySelect = (propertyName: string) => {
    setSelectedProperty(propertyName);
  };

  const createConnection = async () => {
    if (!accessToken || !selectedProperty) {
      setErrorMessage("Please sign in and select a property.");
      return;
    }
    if (!tenant_id || !user_id) {
      setErrorMessage("Tenant ID and User ID are required.");
      return;
    }

    setIsLoading(true);
    try {
      await ga4Service.createConnection({
        refresh_token: refreshToken,
        property_id: selectedProperty.split("/")[1],
        account_id: selectedAccount.split("/")[1],
        tenant_id,
        source: "google_analytics4",
        user_id,
        metadata_status: metadataCache,
      });
      message.success("Connection created successfully.");
      onConnected();
    } catch {
      setErrorMessage("Failed to create connection.");
    } finally {
      setIsLoading(false);
    }
  };

  const testConnection = async () => {
    if (!accessToken || !selectedProperty) {
      setErrorMessage("Please sign in and select a property.");
      return;
    }
    if (!tenant_id || !user_id) {
      setErrorMessage("Tenant ID and User ID are required.");
      return;
    }

    try {
      await ga4Service.testConnection({
        access_token: accessToken,
        property_id: selectedProperty.split("/")[1],
        tenant_id,
        user_id,
      });
      message.success("Connection tested successfully.");
    } catch {
      setErrorMessage("Test connection failed.");
    }
  };

  const removeConnection = async () => {
    setIsLoading(true);
    try {
      if (!tenant_id || !api_key) {throw new Error("Missing credentials");}
      await ga4Service.removeConnection(tenant_id, api_key);
      message.success("Connection removed successfully.");
      setSelectedAccount("");
      setSelectedProperty("");
      setAccessToken("");
      setRefreshToken("");
      setIsGA4Connected(false);
    } catch {
      message.error("Failed to remove connection.");
    } finally {
      setIsLoading(false);
      setModalOpen(false);
    }
  };

  const handleChangeAccount = () => {
    setSelectedAccount("");
    setSelectedProperty("");
    setAccessToken("");
    setRefreshToken("");
    setIsGA4Connected(false);
    setMetadataCache("disabled");
    setAccounts([]);
    setProperties([]);
    message.info("Please sign in with a new account.");
  };

  // ============================
  // Fetch Metadata (on toggle)
  // ============================
  useEffect(() => {
    const fetchMetadata = async () => {
      if (isGA4Connected) {return;}
      if (metadataCache === "enabled" && selectedProperty && accessToken) {
        try {
          await ga4Service.fetchMetadata({
            access_token: accessToken,
            property_id: selectedProperty.split("/")[1],
          });
        } catch {
          setErrorMessage("Error fetching metadata.");
        }
      }
    };

    fetchMetadata();
  }, [metadataCache, selectedProperty, accessToken, isGA4Connected]);

  // ============================
  // Render
  // ============================
 return (
  <>
    {/* Main GA4 Connection Modal */}
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={600}
      className="ga4-modal"
    >
      <div className="innerbody">
        {/* {errorMessage && (
          <div className="error-message-source-connector">
            <span className="error-icon-source-connector">⚠️</span>
            <span>{errorMessage}</span>
          </div>
        )} */}

        <img id="ga4-logo" src="/images/googleanalytics.png" alt="GA4 Icon" />

        <div className="ga4-signin">
          <button
            type="button"
            className="button button--primary"
            onClick={signInWithGoogle}
            disabled={isGA4Connected}
          >
            Sign in with Google Analytics
          </button>
        </div>

        {/* Connection Inputs */}
        <div className="form-input">
          <div className="form-input mt-3">
            <label htmlFor="accessToken">Access Token</label>
            <input
              type="password"
              id="accessToken"
              className="form-control"
              value={accessToken}
              readOnly
            />
          </div>

          <div className="form-input mt-3">
            <label htmlFor="account">Account</label>
            <select
              id="account"
              className="form-select"
              onChange={e => handleAccountSelect(e.target.value)}
              disabled={isGA4Connected}
              value={selectedAccount}
            >
              <option value="">Select Account</option>
              {accounts.map(account => (
                <option key={account.name} value={account.name}>
                  {account.displayNameWithName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-input mt-3">
            <label htmlFor="property">Property</label>
            <select
              id="property"
              className="form-select"
              onChange={e => handlePropertySelect(e.target.value)}
              disabled={isGA4Connected}
              value={selectedProperty}
            >
              <option value="">Select Property</option>
              {properties?.map(property => (
                <option key={property.name} value={property.name}>
                  {property.displayNameWithName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-input mt-3">
            <label htmlFor="metadata-cache">Metadata Cache</label>
            <select
              id="metadata-cache"
              className="form-select"
              value={metadataCache}
              onChange={e => setMetadataCache(e.target.value)}
              disabled={isGA4Connected}
            >
              <option value="disabled">Disable</option>
              <option value="enabled">Enable</option>
            </select>
          </div>
        </div>

        {/* Buttons */}
        <div className="button-group">
          {isGA4Connected ? (
            <>
              <Button
                className="button button--connection"
                onClick={() => setModalOpen(true)}
              >
                Remove Connection
              </Button>
              <Button className="button button--danger" onClick={onClose}>
                Cancel
              </Button>
              <Button
                className="button button--primary"
                onClick={handleChangeAccount}
              >
                Change Account
              </Button>
            </>
          ) : (
            <>
              <Button
                className="button button--connection"
                onClick={createConnection}
                loading={isLoading}
                icon={isLoading ? <SyncOutlined spin /> : null}
              >
                {isLoading ? "Creating Connection" : "Create Connection"}
              </Button>
              <Button className="button button--danger" onClick={onClose}>
                Cancel
              </Button>
              <Button
                className="button button--primary"
                onClick={testConnection}
                loading={isLoading}
                icon={isLoading ? <SyncOutlined spin /> : null}
              >
                {isLoading ? "Testing Connection" : "Test Connection"}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>

    {/* Nested Remove Confirmation Modal */}
    <Modal
      className="logout-modal-content"
      centered
      open={modalOpen}
      footer={null}
      onCancel={() => setModalOpen(false)}
    >
      <div className="logout-modal-wrapper">
        <div className="logout-modal-header">
          <img src="/Icons/Logout Icon.svg" className="logout-Icon" alt="Logout Icon" />
          <span className="popup-msg-content">Remove Connection</span>
        </div>
        <div className="logout-modal-body">
          <span className="popup-msg-content">
            Removing your connection will remove all your existing data from
            this source. Do you want to continue?
          </span>
        </div>
        <div className="logout-modal-footer">
          <Button
            key="confirm"
            className="button button--connection"
            onClick={removeConnection}
            loading={isLoading}
            icon={isLoading ? <SyncOutlined spin /> : null}
          >
            {isLoading ? "Removing" : "Continue"}
          </Button>
          <Button
            key="cancel"
            className="button button--danger"
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  </>
);
};

export default GA4Modal;
